import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Search,
  MoreVertical,
  Eye,
  Ban,
  CheckCircle,
  Trash2,
  Mail,
  Phone,
  Calendar,
  Building2,
  X,
  Download,
  Loader,
  LogIn,
  Activity
} from 'lucide-react';
import { formatDate, exportToCSV } from '../utils/helpers';
import { usersService, switchToUser } from '../services';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import DropdownMenu from '../components/DropdownMenu';
import './UsersPage.css';

const UsersPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  
  // API state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, suspended: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const currentPage = pagination.current;

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        limit: 20
      };
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await usersService.getAll(params);
      
      if (response.success) {
        setUsers(response.data.users || []);
        setStats(response.data.stats || { total: 0, active: 0, pending: 0, suspended: 0 });
        setPagination(response.data.pagination || { current: 1, total: 1, count: 0 });
      } else {
        setError(response.message || t('errors.fetchFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, currentPage, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [userActivityLogs, setUserActivityLogs] = useState([]);
  const [loadingUserLogs, setLoadingUserLogs] = useState(false);

  const handleViewUser = useCallback(async (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    setActiveMenu(null);
    // جلب سجل النشاط للمستخدم
    setLoadingUserLogs(true);
    setUserActivityLogs([]);
    try {
      const res = await api.get(`/admin/activity-logs?userId=${user.id || user._id}&limit=10`);
      if (res.success) setUserActivityLogs(res.data?.logs || []);
    } catch (_) {}
    setLoadingUserLogs(false);
  }, []);

  const handleStatusChange = useCallback(async (userId, newStatus) => {
    try {
      const response = await usersService.changeStatus(userId, newStatus);
      if (response.success) {
        // Refresh the list
        fetchUsers();
        // Close modal if open
        if (showUserModal) {
          setShowUserModal(false);
        }
      } else {
        toast.error(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.updateFailed'));
    }
    setActiveMenu(null);
  }, [fetchUsers, showUserModal, t, toast]);

  const handleDeleteUser = useCallback(async (userId) => {
    if (window.confirm(t('users.confirmDelete'))) {
      try {
        const response = await usersService.delete(userId);
        if (response.success) {
          fetchUsers();
        } else {
          toast.error(response.message || t('errors.deleteFailed'));
        }
      } catch (err) {
        toast.error(err.message || t('errors.deleteFailed'));
      }
    }
    setActiveMenu(null);
  }, [fetchUsers, t, toast]);

  const handleSwitchUser = async (userId) => {
    console.log('Switch user clicked with ID:', userId);
    try {
      const res = await switchToUser(userId);
      console.log('Switch user response:', JSON.stringify(res, null, 2));
      console.log('res.data:', res.data);
      console.log('res.data.accessToken:', res.data?.accessToken);
      console.log('res.data.user:', res.data?.user);

      if (res.success) {
        // Save current admin session so msk-aqar can return to it
        const currentSession = {
          accessToken: localStorage.getItem('msk_admin_access_token'),
          refreshToken: localStorage.getItem('msk_admin_refresh_token'),
          user: localStorage.getItem('msk_admin_user_data'),
          returnUrl: process.env.REACT_APP_ADMIN_APP_URL || 'http://localhost:3002',
          timestamp: new Date().toISOString()
        };
        localStorage.setItem('msk_previous_session', JSON.stringify(currentSession));

        // Store user tokens using msk-aqar's storage keys
        localStorage.setItem('msk_user_access_token', res.data.accessToken);
        localStorage.setItem('msk_user_refresh_token', res.data.refreshToken);
        localStorage.setItem('msk_user_data', JSON.stringify(res.data.user));

        // Open msk-aqar in current tab
        window.location.href = process.env.REACT_APP_USER_APP_URL || 'http://localhost:3001';
      } else {
        toast.error(res.message || 'حدث خطأ في تبديل المستخدم');
      }
    } catch (err) {
      toast.error('حدث خطأ في تبديل المستخدم');
    }
    setActiveMenu(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: t('status.active'), class: 'badge-success' },
      pending: { label: t('status.pending'), class: 'badge-warning' },
      suspended: { label: t('status.suspended'), class: 'badge-danger' }
    };
    return badges[status] || badges.pending;
  };

  // Map API user to expected format
  const mapUser = (user) => ({
    id: user._id,
    name: user.name,
    email: user.email,
    phone: user.phone,
    status: user.isActive ? (user.isVerified ? 'active' : 'pending') : 'suspended',
    offersCount: user.offersCount || 0,
    requestsCount: user.requestsCount || 0,
    followersCount: user.followersCount || 0,
    createdAt: user.createdAt,
    lastLogin: user.lastLogin,
    avatar: user.avatar
  });

  const mappedUsers = users.map(mapUser);

  const handleExport = useCallback(() => {
    const columns = [
      { key: 'name', label: 'الاسم' },
      { key: 'email', label: 'البريد الإلكتروني' },
      { key: 'phone', label: 'رقم الهاتف' },
      { key: 'status', label: 'الحالة' },
      { key: 'offersCount', label: 'عدد العروض' },
      { key: 'requestsCount', label: 'عدد الطلبات' },
      { key: 'createdAt', label: 'تاريخ التسجيل', formatter: formatDate }
    ];
    
    if (mappedUsers.length === 0) {
      toast.warning('لا توجد بيانات للتصدير');
      return;
    }
    
    const success = exportToCSV(mappedUsers, columns, 'users');
    if (success) {
      toast.success(t('common.exportSuccess'));
    } else {
      toast.error(t('errors.exportFailed'));
    }
  }, [mappedUsers, t, toast]);

  return (
    <div className="users-page">
      <div className="page-header">
        <div className="header-title">
          <Users size={28} />
          <div>
            <h1>{t('users.title')}</h1>
            <span className="count">{stats.total} {t('users.user')}</span>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={handleExport}>
            <Download size={18} />
            {t('common.export')}
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div 
          className={`quick-stat ${statusFilter === 'all' ? 'selected' : ''}`} 
          onClick={() => setStatusFilter('all')}
        >
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">{t('users.total')}</span>
        </div>
        <div 
          className={`quick-stat active ${statusFilter === 'active' ? 'selected' : ''}`} 
          onClick={() => setStatusFilter('active')}
        >
          <span className="stat-number">{stats.active}</span>
          <span className="stat-label">{t('status.active')}</span>
        </div>
        <div 
          className={`quick-stat pending ${statusFilter === 'pending' ? 'selected' : ''}`} 
          onClick={() => setStatusFilter('pending')}
        >
          <span className="stat-number">{stats.pending}</span>
          <span className="stat-label">{t('status.pending')}</span>
        </div>
        <div 
          className={`quick-stat suspended ${statusFilter === 'suspended' ? 'selected' : ''}`} 
          onClick={() => setStatusFilter('suspended')}
        >
          <span className="stat-number">{stats.suspended}</span>
          <span className="stat-label">{t('status.suspended')}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="data-section">
        <div className="filters-row">
          <div className="search-wrapper">
            <Search size={20} />
            <input
              type="text"
              className="form-input"
              placeholder={t('users.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t('common.all')}</option>
            <option value="active">{t('status.active')}</option>
            <option value="pending">{t('status.pending')}</option>
            <option value="suspended">{t('status.suspended')}</option>
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <Loader size={32} className="spin" />
            <p>{t('common.loading')}</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-state">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchUsers}>
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Users Table */}
        {!loading && !error && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('users.user')}</th>
                  <th>{t('users.contact')}</th>
                  <th>{t('users.stats')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('users.joinDate')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {mappedUsers.map((user) => {
                  const statusBadge = getStatusBadge(user.status);
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">
                            {user.avatar ? (
                              <img src={user.avatar} alt={user.name} />
                            ) : (
                              <span>{user.name.charAt(0)}</span>
                            )}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{user.name}</span>
                            <span className="user-id">#{user.id.slice(-6)}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-cell">
                          <span className="contact-item">
                            <Mail size={14} />
                            {user.email}
                          </span>
                          <span className="contact-item">
                            <Phone size={14} />
                            {user.phone}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="stats-cell">
                          <span title={t('users.offers')}>
                            <Building2 size={14} />
                            {user.offersCount}
                          </span>
                          <span title={t('users.followers')}>
                            <Users size={14} />
                            {user.followersCount}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${statusBadge.class}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td>
                        <div className="date-cell">
                          <Calendar size={14} />
                          {formatDate(user.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <DropdownMenu
                            isOpen={activeMenu === user.id}
                            onClose={() => setActiveMenu(null)}
                            trigger={
                              <button
                                className="action-btn-icon"
                                onClick={() => setActiveMenu(activeMenu === user.id ? null : user.id)}
                              >
                                <MoreVertical size={18} />
                              </button>
                            }
                          >
                            <button onClick={() => { handleViewUser(user); setActiveMenu(null); }}>
                              <Eye size={16} />
                              {t('common.view')}
                            </button>
                            {user.status === 'active' ? (
                              <button onClick={() => { handleStatusChange(user.id, 'suspended'); setActiveMenu(null); }}>
                                <Ban size={16} />
                                {t('users.suspend')}
                              </button>
                            ) : user.status === 'suspended' ? (
                              <button onClick={() => { handleStatusChange(user.id, 'active'); setActiveMenu(null); }}>
                                <CheckCircle size={16} />
                                {t('users.activate')}
                              </button>
                            ) : (
                              <button onClick={() => { handleStatusChange(user.id, 'active'); setActiveMenu(null); }}>
                                <CheckCircle size={16} />
                                {t('users.approve')}
                              </button>
                            )}
                            <button onMouseDown={() => { handleSwitchUser(user.id); }}>
                              <LogIn size={16} />
                              تبديل للمستخدم
                            </button>
                            <button className="danger" onClick={() => { handleDeleteUser(user.id); setActiveMenu(null); }}>
                              <Trash2 size={16} />
                              {t('common.delete')}
                            </button>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && mappedUsers.length === 0 && (
          <div className="empty-state">
            <Users size={48} />
            <h3>{t('users.noUsers')}</h3>
            <p>{t('users.noUsersDescription')}</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && pagination.total > 1 && (
          <div className="pagination">
            <button 
              className="btn btn-outline" 
              disabled={pagination.current === 1}
              onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">
              {t('common.page')} {pagination.current} {t('common.of')} {pagination.total}
            </span>
            <button 
              className="btn btn-outline" 
              disabled={pagination.current === pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('users.userDetails')}</h2>
              <button className="modal-close" onClick={() => setShowUserModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="user-profile">
                <div className="profile-avatar">
                  {selectedUser.avatar ? (
                    <img src={selectedUser.avatar} alt={selectedUser.name} />
                  ) : (
                    <span>{selectedUser.name.charAt(0)}</span>
                  )}
                </div>
                <div className="profile-info">
                  <h3>{selectedUser.name}</h3>
                  <span className={`badge ${getStatusBadge(selectedUser.status).class}`}>
                    {getStatusBadge(selectedUser.status).label}
                  </span>
                </div>
              </div>

              <div className="user-details-grid">
                <div className="detail-item">
                  <Mail size={18} />
                  <div>
                    <span className="detail-label">{t('auth.email')}</span>
                    <span className="detail-value">{selectedUser.email}</span>
                  </div>
                </div>
                <div className="detail-item">
                  <Phone size={18} />
                  <div>
                    <span className="detail-label">{t('auth.phone')}</span>
                    <span className="detail-value">{selectedUser.phone}</span>
                  </div>
                </div>
                <div className="detail-item">
                  <Calendar size={18} />
                  <div>
                    <span className="detail-label">{t('users.joinDate')}</span>
                    <span className="detail-value">{formatDate(selectedUser.createdAt)}</span>
                  </div>
                </div>
                <div className="detail-item">
                  <Calendar size={18} />
                  <div>
                    <span className="detail-label">{t('users.lastLogin')}</span>
                    <span className="detail-value">
                      {selectedUser.lastLogin ? formatDate(selectedUser.lastLogin) : t('users.never')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="user-stats-grid">
                <div className="stat-box">
                  <Building2 size={24} />
                  <span className="stat-value">{selectedUser.offersCount}</span>
                  <span className="stat-label">{t('users.offers')}</span>
                </div>
                <div className="stat-box">
                  <Users size={24} />
                  <span className="stat-value">{selectedUser.followersCount}</span>
                  <span className="stat-label">{t('users.followers')}</span>
                </div>
                <div className="stat-box">
                  <Eye size={24} />
                  <span className="stat-value">{selectedUser.requestsCount}</span>
                  <span className="stat-label">{t('users.requests')}</span>
                </div>
              </div>
            </div>
            {/* سجل النشاط */}
            <div className="user-activity-section">
              <h4><Activity size={16} /> سجل نشاط الفريق</h4>
              {loadingUserLogs ? (
                <div style={{ padding: '12px', textAlign: 'center' }}><Loader size={18} className="spin" /></div>
              ) : userActivityLogs.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '13px', padding: '8px 0' }}>لا يوجد نشاط مسجل</p>
              ) : (
                <table className="activity-log-table-admin">
                  <thead>
                    <tr><th>العضو</th><th>النشاط</th><th>التفاصيل</th><th>التاريخ</th></tr>
                  </thead>
                  <tbody>
                    {userActivityLogs.map((log) => (
                      <tr key={log._id}>
                        <td>{log.memberName}</td>
                        <td>{log.action}</td>
                        <td style={{ color: '#64748b', fontSize: '12px' }}>{log.targetTitle}</td>
                        <td style={{ color: '#94a3b8', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {new Date(log.createdAt).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="modal-footer">
              {selectedUser.status === 'active' ? (
                <button
                  className="btn btn-danger"
                  onClick={() => handleStatusChange(selectedUser.id, 'suspended')}
                >
                  <Ban size={18} />
                  {t('users.suspend')}
                </button>
              ) : (
                <button
                  className="btn btn-success"
                  onClick={() => handleStatusChange(selectedUser.id, 'active')}
                >
                  <CheckCircle size={18} />
                  {t('users.activate')}
                </button>
              )}
              <button className="btn btn-outline" onClick={() => setShowUserModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
